import { LitElement, html, css, nothing, type PropertyValues } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { api } from "../api";
import type { ProductsResponse, EnvironmentsResponse } from "../types";

const VERSION_RE = /^[A-Za-z0-9][A-Za-z0-9.+-]*$/;

@customElement("create-bundle-dialog")
export class CreateBundleDialog extends LitElement {
  static styles = css`
    :host {
      display: block;
      font-family: Geist, sans-serif;
    }

    .form-field {
      margin-bottom: var(--spacing-16, 16px);
    }

    .form-field label {
      display: block;
      font-size: 13px;
      font-weight: 600;
      color: var(--colors-foreground-fg-gray-700, #5a6072);
      margin-bottom: var(--spacing-4, 4px);
    }

    .form-field .help {
      font-size: 11px;
      color: var(--colors-foreground-fg-gray-500, #98a2b3);
      margin-top: var(--spacing-4, 4px);
    }

    .mfe-table {
      width: 100%;
      border-collapse: collapse;
      font-size: 13px;
      margin-bottom: var(--spacing-8, 8px);
    }

    .mfe-table th {
      text-align: left;
      padding: var(--spacing-4, 4px) var(--spacing-8, 8px);
      border-bottom: 1px solid var(--colors-border-border-gray-300, #e0e3e8);
      font-weight: 600;
      color: var(--colors-foreground-fg-gray-700, #5a6072);
    }

    .mfe-table td {
      padding: var(--spacing-4, 4px) var(--spacing-8, 8px);
      vertical-align: middle;
    }

    .mfe-name {
      font-family: monospace;
      font-size: 12px;
    }

    .version-select {
      font-family: monospace;
      font-size: 12px;
      min-width: 12ch;
    }

    .version-select::part(combobox) {
      font-family: monospace;
      font-size: 12px;
      min-height: 28px;
      padding: 2px 6px;
    }

    .modal-footer {
      display: flex;
      gap: var(--spacing-8, 8px);
      justify-content: flex-end;
    }

    .success-message {
      text-align: center;
      padding: var(--spacing-16, 16px) 0;
    }

    bee-alert {
      margin-bottom: var(--spacing-16, 16px);
    }
  `;

  @property({ type: Boolean }) open = false;
  @property() tenant = "";
  @property() instance = "";
  @property() product = "";
  @property({ type: Array }) plmVersions: string[] = [];
  @property({ type: Number }) activeProductFilter = 0;
  @property({ attribute: false }) products: ProductsResponse | null = null;
  @property({ attribute: false }) environments: EnvironmentsResponse | null = null;

  @state() private _mfeVersions: Map<string, string> = new Map();
  @state() private _compatVersion = "";
  @state() private _applying = false;
  @state() private _error = "";
  @state() private _prUrl = "";
  @state() private _prNumber = 0;

  private _getProdVersions(mfe: string): string[] {
    const prod = this.environments?.prod?.mfes;
    if (!prod) return [];
    const seen = new Set<string>();
    for (const [key, val] of Object.entries(prod)) {
      if (key.replace(/\/\d+$/, "") === mfe && val.resolved) seen.add(val.resolved);
    }
    return [...seen];
  }

  private _getProdVersion(mfe: string): string {
    const prod = this.environments?.prod?.mfes;
    if (!prod) return "";
    const direct = prod[mfe] ?? prod[`${mfe}/0`];
    if (direct) return direct.resolved;
    for (const [key, val] of Object.entries(prod)) {
      if (key.replace(/\/\d+$/, "") === mfe) return val.resolved;
    }
    return "";
  }

  private _getMfes(): string[] {
    if (!this.products) return [];
    const productNames = Object.keys(this.products.products).sort();
    const productKey = productNames[this.activeProductFilter];
    if (!productKey) return [];
    const all = this.products.products[productKey]?.mfes ?? [];
    return all.filter((mfe) => this._getProdVersions(mfe).length > 0);
  }

  private _initMfeVersions() {
    if (!this.products || !this.environments) return;
    const next = new Map<string, string>();
    for (const mfe of this._getMfes()) {
      const v = this._getProdVersion(mfe);
      if (v) next.set(mfe, v);
    }
    this._mfeVersions = next;
  }

  willUpdate(changed: PropertyValues) {
    if (!this.open) return;
    if (changed.has("activeProductFilter")) {
      this._initMfeVersions();
    } else if (
      (changed.has("open") || changed.has("environments") || changed.has("products")) &&
      this._mfeVersions.size === 0
    ) {
      this._initMfeVersions();
    }
  }

  private _handleVersionChange(mfe: string, version: string) {
    const next = new Map(this._mfeVersions);
    if (version) {
      next.set(mfe, version);
    } else {
      next.delete(mfe);
    }
    this._mfeVersions = next;
  }

  private get _isValid(): boolean {
    const selectedVersions = [...this._mfeVersions.entries()].filter(([, v]) => v);
    if (selectedVersions.length === 0) return false;
    for (const [mfe, v] of selectedVersions) {
      if (v.includes("..") || !VERSION_RE.test(v)) return false;
      if (!this._getProdVersions(mfe).includes(v)) return false;
    }
    return true;
  }

  private _close() {
    this._mfeVersions = new Map();
    this._compatVersion = "";
    this._error = "";
    this._prUrl = "";
    this._prNumber = 0;
    this._applying = false;
    this.dispatchEvent(new CustomEvent("close"));
  }

  private async _submit() {
    if (!this._isValid || this._applying) return;
    this._applying = true;
    this._error = "";
    try {
      const mfes: Record<string, string> = {};
      for (const [mfe, ver] of this._mfeVersions.entries()) {
        if (ver) mfes[mfe] = ver;
      }
      const hawaiiProduct = Object.keys(this.products?.products ?? {}).sort()[this.activeProductFilter] ?? "";
      if (!hawaiiProduct) {
        this._error = "Select a product (global product filter) before creating a bundle.";
        return;
      }
      const result = await api.createInstanceBundle({
        tenant: this.tenant,
        instance: this.instance,
        product: this.product,
        compat: this._compatVersion ? { version: this._compatVersion } : undefined,
        mfes,
        hawaiiProduct,
      });
      this._prUrl = result.prUrl;
      this._prNumber = result.prNumber;
      this.dispatchEvent(new CustomEvent("bundle-created", { detail: { prNumber: result.prNumber } }));
    } catch (e: unknown) {
      this._error = e instanceof Error ? e.message : "Failed to create bundle";
    } finally {
      this._applying = false;
    }
  }

  private _renderVersionSelect(mfe: string) {
    const currentValue = this._mfeVersions.get(mfe) ?? "";
    const versions = this._getProdVersions(mfe);

    return html`
      <bee-select
        class="version-select"
        size="small"
        hoist
        placeholder="— pick version —"
        .value=${currentValue}
        @bee-change=${(e: CustomEvent) => this._handleVersionChange(mfe, (e.target as HTMLSelectElement).value)}
      >
        ${versions.map((v) => html`<bee-option value=${v}>${v}</bee-option>`)}
      </bee-select>
    `;
  }

  render() {
    if (!this.open) return nothing;

    if (this._prUrl) {
      return html`
        <bee-modal open in-root title="Bundle PR Created" @bee-request-close=${this._close}>
          <div class="success-message">
            <p>Bundle PR created.</p>
            <p><bee-link href=${this._prUrl} target="_blank" rel="noopener noreferrer">Open PR #${this._prNumber}</bee-link></p>
          </div>
          <div slot="footer" class="modal-footer">
            <bee-button label="Close" bee-type="secondary-grey" @click=${this._close}></bee-button>
          </div>
        </bee-modal>
      `;
    }

    const mfes = this._getMfes();
    const productNames = this.products ? Object.keys(this.products.products).sort() : [];
    const activeProductName = productNames[this.activeProductFilter] ?? "";

    return html`
      <bee-modal open in-root title="Create Bundle — ${this.tenant} / ${this.instance} / ${this.product}" @bee-request-close=${this._close}>
        ${activeProductName ? html`
          <div class="form-field">
            <div class="help">Pinning MFEs for hawaii product: <strong>${activeProductName}</strong></div>
          </div>
        ` : nothing}
        ${mfes.length > 0 ? html`
          <div class="form-field">
            <label>MFE Versions</label>
            <table class="mfe-table">
              <thead>
                <tr>
                  <th>MFE</th>
                  <th>Version</th>
                </tr>
              </thead>
              <tbody>
                ${mfes.map((mfe) => html`
                  <tr>
                    <td><span class="mfe-name">${mfe}</span></td>
                    <td>${this._renderVersionSelect(mfe)}</td>
                  </tr>
                `)}
              </tbody>
            </table>
            <div class="help">Select at least one MFE version.</div>
          </div>
        ` : html`
          <bee-alert variant="info" open><span slot="message">No MFEs found for the active product.</span></bee-alert>
        `}

        ${this.plmVersions.length > 0 ? html`
          <div class="form-field">
            <label>Compat PLM Version (optional)</label>
            <bee-select
              hoist
              placeholder="— none —"
              .value=${this._compatVersion}
              @bee-change=${(e: CustomEvent) => { this._compatVersion = (e.target as HTMLSelectElement).value; }}
            >
              ${this.plmVersions.map((v) => html`<bee-option value=${v}>${v}</bee-option>`)}
            </bee-select>
          </div>
        ` : nothing}

        ${this._error ? html`<bee-alert variant="error" open><span slot="message">${this._error}</span></bee-alert>` : nothing}

        <div slot="footer" class="modal-footer">
          <bee-button label="Cancel" bee-type="secondary-grey" @click=${this._close} ?disabled=${this._applying}></bee-button>
          <bee-button
            label=${this._applying ? "Creating PR..." : "Create Bundle"}
            bee-type="primary"
            @click=${this._submit}
            ?disabled=${!this._isValid || this._applying}
            ?loading=${this._applying}
          ></bee-button>
        </div>
      </bee-modal>
    `;
  }
}
